import type {
  Allocation,
  BudgetMonth,
  Expense,
  Occurrence,
  PersonCost,
} from '@sistemas/contracts'

import { describeDbError, getSupabase } from '@/lib/supabase'
import type { AuditEntry } from '@/modules/projects/store'

/**
 * Escritas de C5, C7 e C10.
 *
 * Mesma regra de C2: grava no banco primeiro, reflete depois. O estado local é
 * cache. Sem isso, a ação parece ter funcionado até o próximo F5 — que é o pior
 * comportamento possível, porque ensina o time a confiar no que não foi salvo.
 */

export type CoreWriteResult<T> = { ok: true; data: T } | { ok: false; reason: string }

function dbError(error: { message?: string } | null, fallback: string): string {
  return describeDbError(error, fallback)
}

function num(value: unknown, fallback: number): number {
  if (value === null || value === undefined) return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * Trilha de auditoria (02 §4).
 *
 * Leitura de custo, troca de responsável, envio ao SGI e toda escrita
 * financeira são auditáveis por requisito. Auditoria que vive em `localStorage`
 * não é auditoria: some com um clique em limpar dados do navegador.
 */
export async function insertAuditEntry(
  input: { action: string; resource: string; detail: string },
  actorId: string,
): Promise<CoreWriteResult<AuditEntry>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('audit_entry')
    .insert({
      actor_id: actorId,
      action: input.action,
      resource: input.resource,
      detail: input.detail,
    })
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Falha ao registrar auditoria.') }
  }
  const row = data as {
    id: string
    actor_id: string | null
    action: string
    resource: string
    detail: string
    occurred_at: string
  }
  return {
    ok: true,
    data: {
      id: row.id,
      action: row.action,
      resource: row.resource,
      detail: row.detail,
      authorId: row.actor_id ?? '',
      at: row.occurred_at,
    },
  }
}

type OccurrenceRow = {
  id: string
  routine_id: string
  date: string
  original_date: string
  status: Occurrence['status']
  agenda: string | null
  minutes: string | null
  attendance: Occurrence['attendance'] | null
}

function toOccurrence(row: OccurrenceRow): Occurrence {
  return {
    id: row.id,
    routineId: row.routine_id,
    date: row.date,
    originalDate: row.original_date,
    status: row.status,
    attendance: row.attendance ?? {},
    ...(row.agenda ? { agenda: row.agenda } : {}),
    ...(row.minutes ? { minutes: row.minutes } : {}),
  }
}

/**
 * Ocorrência de rotina — é aqui que presença e ata viram histórico (C5).
 *
 * A vaga (rotina, data original) é única. Quando ela já existe como
 * `agendada` — criada pela nota do 1:1 do Caderno antes da reunião (migration
 * 027), às vezes já com trecho copiado para a ata —, registrar COMPLETA essa
 * linha: status, presença e a ata digitada aqui vão para o fim da que já
 * estava. Vaga já registrada de outro jeito continua recusada: nada
 * sobrescreve a ata de ninguém em silêncio.
 */
export async function insertOccurrence(input: {
  routineId: string
  date: string
  /** A vaga na série (migration 020). Sem ela, o banco recusa o insert. */
  originalDate: string
  status: Occurrence['status']
  minutes?: string | undefined
  agenda?: string | undefined
  attendance: Occurrence['attendance']
}): Promise<CoreWriteResult<Occurrence>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('occurrence')
    .insert({
      routine_id: input.routineId,
      date: input.date,
      original_date: input.originalDate,
      status: input.status,
      minutes: input.minutes || null,
      agenda: input.agenda || null,
      attendance: input.attendance,
    })
    .select('*')
    .single()

  if (error && (error as { code?: string }).code === '23505') {
    return completeScheduledOccurrence(input)
  }
  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível registrar a ocorrência.') }
  }
  return { ok: true, data: toOccurrence(data as OccurrenceRow) }
}

async function completeScheduledOccurrence(input: {
  routineId: string
  date: string
  originalDate: string
  status: Occurrence['status']
  minutes?: string | undefined
  agenda?: string | undefined
  attendance: Occurrence['attendance']
}): Promise<CoreWriteResult<Occurrence>> {
  const sb = getSupabase()
  const existing = await sb
    .from('occurrence')
    .select('*')
    .eq('routine_id', input.routineId)
    .eq('original_date', input.originalDate)
    .single()
  if (existing.error || !existing.data) {
    return { ok: false, reason: dbError(existing.error, 'Não foi possível registrar a ocorrência.') }
  }
  const row = existing.data as OccurrenceRow
  if (row.status !== 'agendada') {
    return { ok: false, reason: 'Esta ocorrência já foi registrada.' }
  }
  const minutes = [row.minutes?.trim(), input.minutes?.trim()].filter(Boolean).join('\n\n') || null
  const { data, error } = await sb
    .from('occurrence')
    .update({
      date: input.date,
      status: input.status,
      minutes,
      agenda: input.agenda || row.agenda,
      attendance: input.attendance,
    })
    .eq('id', row.id)
    .select('*')
    .single()
  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível registrar a ocorrência.') }
  }
  return { ok: true, data: toOccurrence(data as OccurrenceRow) }
}

/** Budget mensal — `month` é a chave primária, então redefinir é upsert. */
export async function upsertBudget(
  month: string,
  amountBrl: number,
  authorId: string,
): Promise<CoreWriteResult<BudgetMonth>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('budget_month')
    // Editar à mão marca o mês como `manual`: o budget padrão não o reescreve mais.
    .upsert({ month, amount: amountBrl, updated_by: authorId, source: 'manual' }, { onConflict: 'month' })
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível salvar o budget.') }
  }
  const row = data as { month: string; amount: number | string; source: string | null }
  return {
    ok: true,
    data: {
      id: row.month,
      month: row.month,
      budgetBrl: num(row.amount, 0),
      isManual: row.source !== 'padrao',
    },
  }
}

/**
 * Grava o budget padrão do ano e o aplica aos meses do corrente em diante que
 * não foram editados à mão (RPC `apply_budget_default`, migration budget_v2).
 * Devolve quantos meses receberam o padrão.
 */
export async function applyBudgetDefault(
  year: number,
  amountBrl: number,
): Promise<CoreWriteResult<number>> {
  const { data, error } = await getSupabase().rpc('apply_budget_default', {
    p_year: year,
    p_amount: amountBrl,
  })
  if (error) return { ok: false, reason: dbError(error, 'Não foi possível aplicar o budget padrão.') }
  return { ok: true, data: Number(data ?? 0) }
}

/** Os tetos de um ano, relidos depois de a RPC do padrão escrever vários meses. */
export async function fetchBudgetMonths(year: number): Promise<CoreWriteResult<BudgetMonth[]>> {
  const { data, error } = await getSupabase()
    .from('budget_month')
    .select('month, amount, source')
    .like('month', `${year}-%`)
  if (error) return { ok: false, reason: dbError(error, 'Não foi possível reler o budget.') }
  return {
    ok: true,
    data: ((data ?? []) as { month: string; amount: number | string; source: string | null }[]).map((r) => ({
      id: r.month,
      month: r.month,
      budgetBrl: num(r.amount, 0),
      isManual: r.source !== 'padrao',
    })),
  }
}

export async function fetchBudgetDefault(year: number): Promise<CoreWriteResult<number | null>> {
  const { data, error } = await getSupabase()
    .from('budget_default')
    .select('amount')
    .eq('year', year)
    .maybeSingle()
  if (error) return { ok: false, reason: dbError(error, 'Não foi possível ler o budget padrão.') }
  return { ok: true, data: data ? num((data as { amount: number | string }).amount, 0) : null }
}

export async function insertExpense(
  input: Omit<Expense, 'id' | 'createdBy'>,
  authorId: string,
): Promise<CoreWriteResult<Expense>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('expense')
    .insert({
      // `description` é NOT NULL desde a migration de finanças: em branco, o
      // fornecedor. Mandar `null` fazia o lançamento falhar calado (SIS-60).
      description: input.description?.trim() || input.vendor,
      vendor: input.vendor,
      category: input.category || null,
      amount: input.originalAmount,
      currency: input.currency,
      amount_brl: input.amountBrl,
      fx_rate: input.fxRate,
      fx_rate_date: input.fxRateDate || null,
      fx_source: input.fxSource || null,
      receipt_name: input.receiptName || null,
      incurred_on: input.paidAt,
      // `month` é redundante com `incurred_on` de propósito: o fechamento
      // mensal agrupa por ele, e derivar a cada consulta impediria índice.
      month: input.paidAt.slice(0, 7),
      splits: input.splits,
      recurring: input.recurring,
      renewal_date: input.renewalDate || null,
      created_by: authorId,
    })
    .select('id')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível lançar a despesa.') }
  }
  return { ok: true, data: { ...input, id: (data as { id: string }).id, createdBy: authorId } }
}

/**
 * SIS-63: lança o TOTAL em `count` parcelas mensais numa transação só (RPC).
 * A divisão, as datas e a conversão pela PTAX da compra são do banco; aqui só
 * se devolvem as linhas que ele gravou, na ordem das parcelas.
 */
export async function insertExpenseInstallments(
  input: Omit<Expense, 'id' | 'createdBy' | 'amountBrl' | 'installment' | 'splits' | 'recurring' | 'renewalDate'>,
  count: number,
): Promise<CoreWriteResult<Expense[]>> {
  const sb = getSupabase()
  const { data, error } = await sb.rpc('create_expense_installments', {
    p_vendor: input.vendor,
    p_description: input.description?.trim() || null,
    p_category: input.category || null,
    p_currency: input.currency,
    p_total: input.originalAmount,
    p_fx_rate: input.currency === 'BRL' ? null : input.fxRate,
    p_fx_source: input.fxSource || null,
    p_receipt_name: input.receiptName || null,
    p_first_on: input.paidAt,
    p_count: count,
  })
  if (error || !Array.isArray(data) || data.length !== count) {
    return { ok: false, reason: dbError(error, 'Não foi possível lançar o parcelamento.') }
  }
  type Row = {
    id: string; description: string | null; vendor: string; category: string | null
    amount: number | string; currency: Expense['currency']; amount_brl: number | string
    fx_rate: number | string | null; fx_rate_date: string | null; fx_source: string | null
    receipt_name: string | null; incurred_on: string; created_by: string
    installment_group: string; installment_number: number; installment_count: number
  }
  const rows = (data as Row[]).sort((a, b) => a.installment_number - b.installment_number)
  return {
    ok: true,
    data: rows.map((r) => ({
      id: r.id,
      paidAt: r.incurred_on,
      vendor: r.vendor,
      category: r.category ?? '',
      currency: r.currency,
      originalAmount: num(r.amount, 0),
      fxRate: num(r.fx_rate, 1),
      fxRateDate: r.fx_rate_date ?? r.incurred_on,
      fxSource: r.fx_source ?? '',
      amountBrl: num(r.amount_brl, 0),
      receiptName: r.receipt_name ?? '',
      splits: [],
      recurring: false,
      createdBy: r.created_by,
      ...(r.description ? { description: r.description } : {}),
      installment: { group: r.installment_group, number: r.installment_number, count: r.installment_count },
    })),
  }
}

/** SIS-63: o parcelamento sai inteiro — as parcelas dependem do comprovante da 1ª. */
export async function deleteExpenseGroup(group: string): Promise<CoreWriteResult<true>> {
  const sb = getSupabase()
  const { error } = await sb.from('expense').delete().eq('installment_group', group)
  if (error) return { ok: false, reason: dbError(error, 'Não foi possível excluir o parcelamento.') }
  return { ok: true, data: true }
}

export async function deleteExpense(id: string): Promise<CoreWriteResult<true>> {
  const sb = getSupabase()
  const { error } = await sb.from('expense').delete().eq('id', id)
  if (error) return { ok: false, reason: dbError(error, 'Não foi possível excluir a despesa.') }
  return { ok: true, data: true }
}

/** Reconhecer alerta é ato de pessoa: fica registrado com autor e horário. */
export async function acknowledgeAlertRemote(
  alertId: string,
  memberId: string,
): Promise<CoreWriteResult<true>> {
  const sb = getSupabase()
  const { error } = await sb
    .from('alert_ack')
    .upsert({ alert_id: alertId, acknowledged_by: memberId }, { onConflict: 'alert_id' })
  if (error) {
    return { ok: false, reason: dbError(error, 'Não foi possível reconhecer o alerta.') }
  }
  return { ok: true, data: true }
}

/**
 * Custo tem vigência (05-R6): valor novo é linha nova, nunca UPDATE.
 * Sobrescrever apagaria a resposta para "quanto custava o time em junho".
 */
export async function insertPersonCost(
  input: {
    memberId: string
    effectiveFrom: string
    salary?: number | undefined
    charges?: number | undefined
    benefits?: number | undefined
  },
  authorId: string,
): Promise<CoreWriteResult<PersonCost>> {
  const sb = getSupabase()
  // `monthly_total` é coluna GERADA no banco (salário + encargos + benefícios).
  // Enviar valor daqui é rejeitado pelo Postgres — e ainda bem: o total
  // plenamente carregado é regra de domínio, e regra de domínio mora no banco.
  const { data, error } = await sb
    .from('person_cost')
    .insert({
      member_id: input.memberId,
      effective_from: input.effectiveFrom,
      salary: input.salary ?? 0,
      charges: input.charges ?? 0,
      benefits: input.benefits ?? 0,
      created_by: authorId,
    })
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível salvar o custo.') }
  }
  const row = data as {
    member_id: string
    effective_from: string
    salary: number | string | null
    charges: number | string | null
    benefits: number | string | null
    monthly_total: number | string | null
  }
  return {
    ok: true,
    data: {
      memberId: row.member_id,
      effectiveFrom: row.effective_from,
      monthlyTotal: num(row.monthly_total, 0),
      ...(row.salary !== null ? { salary: num(row.salary, 0) } : {}),
      ...(row.charges !== null ? { charges: num(row.charges, 0) } : {}),
      ...(row.benefits !== null ? { benefits: num(row.benefits, 0) } : {}),
    },
  }
}

export async function deletePersonCost(
  memberId: string,
  effectiveFrom: string,
): Promise<CoreWriteResult<true>> {
  const sb = getSupabase()
  const { error } = await sb
    .from('person_cost')
    .delete()
    .eq('member_id', memberId)
    .eq('effective_from', effectiveFrom)
  if (error) {
    return { ok: false, reason: dbError(error, 'Não foi possível remover a vigência.') }
  }
  return { ok: true, data: true }
}

/** Alocação percentual mensal — base do custo por projeto (05 §5). */
export async function upsertAllocation(input: {
  memberId: string
  projectId: string
  month: string
  percent: number
}): Promise<CoreWriteResult<Allocation | null>> {
  const sb = getSupabase()

  // Zerar é remover: guardar 0% criaria linha que só confunde o rateio.
  if (input.percent <= 0) {
    const { error } = await sb
      .from('allocation')
      .delete()
      .eq('member_id', input.memberId)
      .eq('project_id', input.projectId)
      .eq('month', input.month)
    if (error) {
      return { ok: false, reason: dbError(error, 'Não foi possível remover a alocação.') }
    }
    return { ok: true, data: null }
  }

  const { data, error } = await sb
    .from('allocation')
    .upsert(
      {
        member_id: input.memberId,
        project_id: input.projectId,
        month: input.month,
        percent: input.percent,
      },
      { onConflict: 'member_id,project_id,month' },
    )
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível salvar a alocação.') }
  }
  const row = data as {
    id: string
    member_id: string
    project_id: string
    month: string
    percent: number | string
  }
  return {
    ok: true,
    data: {
      id: row.id,
      memberId: row.member_id,
      projectId: row.project_id,
      month: row.month,
      percent: num(row.percent, 0),
    },
  }
}
